import { getFormErrorMessage } from "@core/utility/get-error-message";
import { ClientFormRule, ClientFormSchema } from "@factory/form/form.rule";
import { useFormDefaultClient } from "@factory/form/form-default-client.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseClientModel } from "@factory/model/query-model-client";
import { 
  useQyDeleteClient, 
  useQyGetClientById, 
  useQyUpdateClient} from "@factory/query/client.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditClient() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultClient();
  const navigate = useNavigate();
  const { mapUpdateClient, mapDeleteClient } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { clientId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Client
  const handleGetApiSuccess = (data: GetResponseClientModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("name", responseData?.name || "");
      setValue("email", responseData?.email || "");
      setValue("address", responseData?.address || "");
      setValue("contactNumber", responseData?.contact_number || "");
  };
  const {
    data: clientResponse,
    isLoading,
    isError: clientError,
  } = useQyGetClientById(clientId || "", handleGetApiSuccess);
  const clientData = clientResponse?.data?.[0];

  // API UPDATE Client
  const handleUpdateApiSuccess = () => {
    showSuccess("Client updated successfully");
  };
  const { mutate: updateClient, isLoading: isEditLoading } = useQyUpdateClient(
    handleUpdateApiSuccess
  );

  // API DELETE Client
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Client deleted successfully");
  };
  const { mutate: deleteClient } = useQyDeleteClient(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<ClientFormSchema>({
    defaultValues: getDefault(clientData),
    resolver: zodResolver(ClientFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: ClientFormSchema) => {
    if (!clientData || !clientData.code) {
      throw new Error("No client data");
    }

    const formData = mapUpdateClient(form, clientData.code);
    updateClient(formData);
  };
  const handleValidateError = (err: FieldErrors<ClientFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!clientData || !clientData.code) {
      throw new Error("No client data found");
    }

    const form = mapDeleteClient(clientData.code);
    deleteClient(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    clientId,
    clientData,
    isLoading,
    clientError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}